import {
  CatalogEdge,
  CatalogFlow,
  CatalogNode,
  controllerFleetId,
  nextCatalogId,
} from './catalogDoc';
import { GraphDevice } from './graph';
import { PeripheryWidgetType } from './peripheryBundle';

export type CatalogWizardArgs = {
  flowName: string;
  hostname: string;
  controllerId: string;
  device: GraphDevice;
  widget: PeripheryWidgetType;
  interval?: number;
  /** Dashboard switch icon: toggle or bulb. */
  icon?: 'switch' | 'bulb';
  /** stacklight-legacy → modern stacklight (optional second host). */
  wireLocalActuators?: boolean;
  wireZigbeeUplink?: boolean;
  modernHostname?: string;
  modernControllerId?: string;
  modernOf?: string;
  /** stacklight: definition (signal file id) for the instance block. */
  signal?: string;
};

export type ChainState = {
  nodes: CatalogNode[];
  edges: CatalogEdge[];
  x0: number;
  y0: number;
};

export function originOf(doc: CatalogFlow): { x0: number; y0: number } {
  const nodes = doc.nodes || [];
  const originX = nodes.reduce((max, node) => Math.max(max, node.x), 0);
  return { x0: nodes.length ? originX + 80 : 40, y0: 80 };
}

export function startChain(doc: CatalogFlow): ChainState {
  const { x0, y0 } = originOf(doc);
  return { nodes: [...(doc.nodes || [])], edges: [...(doc.edges || [])], x0, y0 };
}

export function finishChain(doc: CatalogFlow, args: CatalogWizardArgs, state: ChainState): CatalogFlow {
  return {
    ...doc,
    id: doc.id || args.flowName,
    enable: doc.enable !== false,
    nodes: state.nodes,
    edges: state.edges,
  };
}

export function link(state: ChainState, from: string, to: string, both = false) {
  const existing = state.edges.find((edge) => edge.from === from && edge.to === to);
  if (existing) {
    if (both) {
      existing.both = true;
    }
    return;
  }
  state.edges.push(both ? { from, to, both: true } : { from, to });
}

function push(state: ChainState, node: CatalogNode): CatalogNode {
  state.nodes.push(node);
  return node;
}

export function ensureController(state: ChainState, args: CatalogWizardArgs): CatalogNode {
  const existing = state.nodes.find((node) => (
    node.type === 'controller'
    && (
      controllerFleetId(node) === args.controllerId
      || node.hostname === args.hostname
      || node.owner === args.controllerId
    )
  ));
  if (existing) {
    return existing;
  }
  return push(state, {
    id: nextCatalogId('g', state.nodes),
    type: 'controller',
    x: state.x0,
    y: state.y0,
    owner: args.controllerId,
    hostname: args.hostname,
  });
}

export function ensureBus(
  state: ChainState,
  args: { of: string; busType: string; role?: string; x: number; y: number },
): CatalogNode {
  const existing = state.nodes.find((node) => (
    node.type === 'bus'
    && node.busType === args.busType
    && (node.of === args.of || !node.of)
    && (!args.role || node.role === args.role || !node.role)
  ));
  if (existing) {
    return existing;
  }
  return push(state, {
    id: nextCatalogId('bus', state.nodes),
    type: 'bus',
    x: args.x,
    y: args.y,
    of: args.of,
    busType: args.busType,
    role: args.role,
  });
}

export function ensureDevice(
  state: ChainState,
  args: { of: string; deviceType: string; x: number; y: number },
): CatalogNode {
  const existing = state.nodes.find((node) => node.type === 'device' && node.of === args.of);
  if (existing) {
    return existing;
  }
  return push(state, {
    id: nextCatalogId('dev', state.nodes),
    type: 'device',
    x: args.x,
    y: args.y,
    of: args.of,
    deviceType: args.deviceType,
  });
}

export function ensureTimer(state: ChainState, interval: number, x: number, y: number): CatalogNode {
  const existing = state.nodes.find((node) => node.type === 'timer' && (node.interval || 60) === interval && !node.interval_ms);
  if (existing) {
    return existing;
  }
  return push(state, {
    id: nextCatalogId('tick', state.nodes),
    type: 'timer',
    x,
    y,
    interval,
  });
}

export function ensureTimerMs(state: ChainState, intervalMs: number, x: number, y: number): CatalogNode {
  const ms = Math.max(1, Math.round(intervalMs));
  const existing = state.nodes.find((node) => node.type === 'timer' && Number(node.interval_ms) === ms);
  if (existing) {
    return existing;
  }
  return push(state, {
    id: nextCatalogId('tick', state.nodes),
    type: 'timer',
    x,
    y,
    interval_ms: ms,
  });
}

export function ensurePoint(
  state: ChainState,
  args: { of: string; key: string; poll?: boolean; x: number; y: number; deviceId?: string },
): CatalogNode {
  const existing = state.nodes.find((node) => node.type === 'point' && node.of === args.of && node.key === args.key);
  if (existing) {
    if (args.deviceId) {
      link(state, `${args.deviceId}.out`, `${existing.id}.dev`);
    }
    return existing;
  }
  const node = push(state, {
    id: nextCatalogId('p', state.nodes),
    type: 'point',
    x: args.x,
    y: args.y,
    of: args.of,
    key: args.key,
    poll: args.poll,
  });
  if (args.deviceId) {
    link(state, `${args.deviceId}.out`, `${node.id}.dev`);
  }
  return node;
}

export function ensureMqtt(state: ChainState, topic: string, x: number, y: number): CatalogNode {
  const existing = state.nodes.find((node) => node.type === 'mqtt_topic' && node.topic === topic);
  if (existing) {
    return existing;
  }
  return push(state, {
    id: nextCatalogId('top', state.nodes),
    type: 'mqtt_topic',
    x,
    y,
    topic,
  });
}

export function ensureDashboard(
  state: ChainState,
  args: { widgetType: string; title: string; signalName?: string; x: number; y: number },
): CatalogNode {
  const existing = state.nodes.find((node) => (
    node.type === 'dashboard' && node.widgetType === args.widgetType
  ));
  if (existing) {
    return existing;
  }
  return addDashboard(state, args);
}

export function addDashboard(
  state: ChainState,
  args: {
    widgetType: string;
    title: string;
    signalName?: string;
    icon?: string;
    channelNames?: string[];
    x: number;
    y: number;
  },
): CatalogNode {
  return push(state, {
    id: nextCatalogId('dash', state.nodes),
    type: 'dashboard',
    x: args.x,
    y: args.y,
    widgetType: args.widgetType,
    title: args.title,
    signalName: args.signalName,
    icon: args.icon,
    channelNames: args.channelNames,
  });
}

export function ensureLabel(
  state: ChainState,
  args: { layout: string; widget: string; x: number; y: number },
): CatalogNode {
  const existing = state.nodes.find((node) => (
    node.type === 'label' && node.layout === args.layout && node.widget === args.widget
  ));
  if (existing) {
    return existing;
  }
  return push(state, {
    id: nextCatalogId('lbl', state.nodes),
    type: 'label',
    x: args.x,
    y: args.y,
    layout: args.layout,
    widget: args.widget,
  });
}

/** Poll point → optional calc → mqtt publish. Timer only when poll is true. */
export function wireToMqtt(
  state: ChainState,
  args: {
    point: CatalogNode;
    topic: string;
    y: number;
    xCalc: number;
    xMqtt: number;
    timer?: CatalogNode;
    scale?: { op: string; value: number };
    from?: 'value' | 'event';
    /** Point value and the topic are one cable: publish out, subscribe back into the point. */
    both?: boolean;
  },
): CatalogNode {
  const mqtt = ensureMqtt(state, args.topic, args.xMqtt, args.y);
  const fromValue = `${args.point.id}.${args.from || 'value'}`;
  if (state.edges.some((edge) => edge.from === fromValue)) {
    return mqtt;
  }
  if (args.timer && args.point.poll) {
    link(state, `${args.timer.id}.trigger`, `${args.point.id}.trigger`);
  }
  if (args.scale) {
    const calc = push(state, {
      id: nextCatalogId('calc', state.nodes),
      type: 'calc',
      x: args.xCalc,
      y: args.y,
      op: args.scale.op,
      value: args.scale.value,
    });
    link(state, fromValue, `${calc.id}.in`);
    link(state, `${calc.id}.out`, `${mqtt.id}.publish`, args.both === true);
  } else {
    link(state, fromValue, `${mqtt.id}.publish`, args.both === true);
  }
  return mqtt;
}

export function upsertDevice(values: Record<string, unknown>, instance: GraphDevice): Record<string, unknown>[] {
  const devices = Array.isArray(values.devices)
    ? values.devices.filter((item) => item && typeof item === 'object' && !Array.isArray(item)) as Record<string, unknown>[]
    : [];
  const index = devices.findIndex((row) => String(row.id) === instance.id);
  if (index >= 0) {
    devices[index] = { ...devices[index], ...instance };
  } else {
    devices.push(instance);
  }
  return devices;
}

export function asGroup(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : {};
}
